import React, { useEffect, useRef, useState } from 'react';
import ReactCrop, {
  Crop,
  PixelCrop,
  centerCrop,
  makeAspectCrop,
} from 'react-image-crop';
import { canvasPreview } from '../../../../helpers/canvasPreview';
import { useDebounceEffect } from '../../../../helpers/useDebounceEffect';
import 'react-image-crop/dist/ReactCrop.css';
import {
  Modal,
  ModalContent,
  ButtonBlockWrapper,
  ModalWrapper,
  ImgFullWrapper,
} from './styled.cropper';
import { ButtonBlockColumn } from './../../sharedStyledComponents/GenericStyles.styled';
import { ButtonLarge } from './../buttons/ButtonLarge';
import { useTranslation } from 'react-i18next';
import {
  formatDateTime,
  processFile,
} from '../../../../helpers/convertFileName';

function centerAspectCrop (
  mediaWidth: number,
  mediaHeight: number,
  aspect: number
) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

export interface ICropper {
  onImageCompletion: any;
  imageFile: File | null;
  previousImages?: any;
}

export function Cropper ({
  onImageCompletion,
  imageFile,
  previousImages,
}: ICropper) {
  const [imageSource, setImgageSource] = useState('');
  const [showCropper, setShowCropper] = useState(false);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [crop, setCrop] = useState<Crop | undefined>({
    unit: '%',
    x: 25,
    y: 25,
    width: 50,
    height: 50,
  });
  const { t } = useTranslation();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();

  useEffect(() => {
    if (!imageFile) {
      return;
    }
    setCrop(undefined); // Makes crop preview update between images.
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      if (reader && reader.result) {
        setImgageSource(reader.result.toString() || '');
      }
    });
    reader.readAsDataURL(imageFile);
  }, [imageFile]);

  function onImageLoad (e: React.SyntheticEvent<HTMLImageElement>) {
    const { width, height } = e.currentTarget;
    setCrop(centerAspectCrop(width, height, 16 / 9));
  }

  async function onCropCompletion () {
    const image = imageRef.current;
    const previewCanvas = previewCanvasRef.current;
    if (!image || !previewCanvas || !completedCrop) {
      throw new Error('Crop canvas does not exist');
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const canvas = document.createElement('canvas');
    canvas.width = completedCrop.width * scaleX;
    canvas.height = completedCrop.height * scaleY;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('No 2d context');
    }

    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      canvas.width,
      canvas.height,
      0,
      0,
      canvas.width,
      canvas.height
    );

    const fileObj = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob: Blob | null) => {
          if (!blob) {
            reject(new Error('Canvas is empty'));
            return;
          }
          if (!imageFile) {
            return;
          }
          let fileName = imageFile.name;
          if (previousImages.includes(fileName)) {
            const formattedDate = formatDateTime();
            fileName =
              imageFile.name.replace(/\.[^/.]+$/, '') +
              '-' +
              formattedDate +
              '.jpeg';
          }
          const file = new File([blob], fileName, { type: 'image/jpeg' });

          resolve(file);
        },
        'image/jpeg',
        0.7
      );
    });

    onImageCompletion(fileObj);
  }

  useDebounceEffect(
    async () => {
      if (
        completedCrop &&
        completedCrop.width &&
        completedCrop.height &&
        imageRef.current &&
        previewCanvasRef.current
      ) {
        // We use canvasPreview as it's much faster than imgPreview.
        canvasPreview(
          imageRef.current,
          previewCanvasRef.current,
          completedCrop
        );
      }
    },
    100,
    [completedCrop]
  );
  return (
    <>
      {imageSource && (
        <div className="react-crop" id="react-confirm-alert">
          <Modal className="react-confirm-alert-overlay overlayPrompt">
            <ModalWrapper>
              <ModalContent>
                {imageSource && showCropper && (
                  <ReactCrop
                    crop={crop}
                    onChange={(_, percentCrop) => setCrop(percentCrop)}
                    onComplete={(croppedImage) =>
                      setCompletedCrop(croppedImage)
                    }
                    ruleOfThirds={true}
                    keepSelection={true}
                  >
                    <img
                      ref={imageRef}
                      alt=""
                      src={imageSource}
                      onLoad={onImageLoad}
                      className="justify-image-center"
                    />
                  </ReactCrop>
                )}
                {!showCropper && (
                  <ImgFullWrapper>
                    <img
                      ref={imageRef}
                      alt=""
                      src={imageSource}
                      className="justify-image-center"
                    />
                  </ImgFullWrapper>
                )}
                {completedCrop && (
                  <>
                    <div>
                      <canvas
                        ref={previewCanvasRef}
                        style={{
                          border: '1px solid black',
                          objectFit: 'contain',
                          width: completedCrop.width,
                          height: completedCrop.height,
                          display: 'none',
                        }}
                      />
                    </div>
                  </>
                )}

                <ButtonBlockWrapper>
                  <ButtonBlockColumn>
                    <ButtonLarge
                      title={
                        showCropper
                          ? t('COMMON.MY_AUDIT.BUTTON.FULL_IMAGE')
                          : t('COMMON.MY_AUDIT.BUTTON.CROP_IMAGE')
                      }
                      onClick={() => {
                        setShowCropper(!showCropper);
                      }}
                    />
                    <ButtonLarge
                      title={t('COMMON.MY_AUDIT.BUTTON.DONE')}
                      backgroundColor="verdigris"
                      textColor="oxfordBlue"
                      onClick={() => {
                        setImgageSource('');
                        if (showCropper) {
                          onCropCompletion();
                        } else {
                          if (!imageFile) {
                            return;
                          }
                          const updateFile = processFile(
                            imageFile,
                            previousImages
                          );
                          onImageCompletion(updateFile);
                        }
                      }}
                    />
                    <ButtonLarge
                      onClick={() => {
                        setImgageSource('');
                      }}
                      title={t('COMMON.MY_AUDIT.BUTTON.CANCEL')}
                      outlined={true}
                      inverted={true}
                    />
                  </ButtonBlockColumn>
                </ButtonBlockWrapper>
              </ModalContent>
            </ModalWrapper>
          </Modal>
        </div>
      )}
    </>
  );
}
